import React from 'react'
import { fmtDelta } from '../home/model'
import { BLOCKED_DELTA_COLOR, blockedCaption, categoryMeta, count, rate } from './model'

// The four headline numbers. Every delta is measured against
// `range.comparison`, printed exactly as the backend says it.
export default function BlockedKpiTiles({ kpis, comparison, reasonFiltered }) {
  const top = kpis.top_reason
  const partner = kpis.most_blocked_partner
  const blocked = Number(kpis.blocked?.value || 0)
  const fixable = Number(kpis.fixable?.value || 0)

  return (
    <section aria-label="Key figures" className="bk-kpis">
      <Tile
        label="Blocked"
        value={count(blocked)}
        caption={blockedCaption(kpis, reasonFiltered)}
        delta={kpis.blocked?.delta_pct}
        comparison={comparison}
      />
      <Tile
        label="Top reason"
        swatch={top ? categoryMeta(top.category).color : undefined}
        value={top ? top.label : '—'}
        small
        caption={top ? `${count(top.count)} · ${rate(top.pct)} of blocked` : 'Nothing blocked'}
      />
      <Tile
        label="Most blocked partner"
        value={partner ? partner.name : '—'}
        small
        caption={partner ? `${count(partner.blocked)} blocked · ${rate(partner.rate)} block rate` : 'Nothing blocked'}
      />
      <Tile
        label="Fixable by partners"
        value={count(fixable)}
        caption={blocked > 0 ? `${rate(fixable / blocked)} of blocked · data errors` : 'Data errors a partner can correct'}
        delta={kpis.fixable?.delta_pct}
        comparison={comparison}
      />
    </section>
  )
}

function Tile({ label, value, caption, swatch, delta, comparison, small = false }) {
  return (
    <div className="card" data-testid={`kpi-${label}`} style={{ padding: 18, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 6 }}>
        {swatch && <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: swatch }} />}
        {label}
      </div>
      <div
        title={small ? String(value) : undefined}
        style={{
          fontSize: small ? 20 : 26, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2,
          // A reason or partner name wraps to two lines before it is cut.
          ...(small
            ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }
            : { whiteSpace: 'nowrap' }),
        }}
      >
        {value}
      </div>
      {caption && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{caption}</div>}
      <Delta deltaPct={delta} comparison={comparison} />
    </div>
  )
}

// "↑ 12.3% vs 7-day avg", red when more got blocked. An absent delta (no
// baseline) shows nothing at all.
function Delta({ deltaPct, comparison }) {
  const d = fmtDelta(deltaPct)
  if (!d) return null
  return (
    <div data-testid="delta" style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 12, marginTop: 6, flexWrap: 'wrap' }}>
      <span data-tone={d.tone} style={{ fontWeight: 700, color: BLOCKED_DELTA_COLOR[d.tone], fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{d.text}</span>
      {comparison && <span style={{ color: 'var(--text-muted)' }}>{comparison}</span>}
    </div>
  )
}
